import React from "react";
import {  ScrollView, StyleSheet, Text, View } from "react-native";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useGlobalState } from "../../shared/state";
import { I18N } from "../../shared/util";
import { Fonts, Styles } from "../../styles";

const PrivacyPolicy: React.FC = (): JSX.Element => {
  const [language] = useGlobalState("language");
  const I18n = I18N(language);

  const renderList = (items: string[]) =>
    items.map((item, index) => (
      <Text key={index} style={styles.bullet}>
        • {item}
      </Text>
    ));

  const renderRights = (titles: string[], explanations: string[]) =>
  titles.map((title, index) => (
    <Text key={index} style={styles.paragraph}>
      <Text style={styles.rightTitle}>{title.replace(/\.$/, "")}: </Text>
      {explanations[index]}
    </Text>
  ));

  const renderCompanyCell = (prefix: "company1" | "company2") => {
    const base = `privacyPolicy.sections.who.${prefix}`;
    const name = String(I18n.t(`${base}.name`));
    const addressRaw = I18n.t(`${base}.address`);
    const addressLines = Array.isArray(addressRaw)
      ? (addressRaw as unknown[]).map(String)
      : [];
    return (
      <>
        <Text style={styles.tableCellTitle}>{name}</Text>
        {addressLines.map((line, i) => (
          <Text key={i} style={styles.tableCellText}>
            {line}
          </Text>
        ))}
      </>
    );
  };

  const renderResponsibilityCell = (prefix: "company1" | "company2") => (
    <Text style={styles.tableCellText}>
      {I18n.t(`privacyPolicy.sections.who.${prefix}.responsibility`) as string}
    </Text>
  );

  return (
    <LayoutWrapper
      showHeaderCloseButton={false}
      showMenuButtons={false}
      hideInfoButton
    >
      
      <View style={[Styles.contentWrapperFull, Styles.column]}>
        <View style={styles.wrapper}>

          {/* HEADER */}
          <View style={styles.header}>
            <Text style={styles.title}>
              {I18n.t("privacyPolicy.title").toUpperCase()}
            </Text>
          </View>

          {/* CONTENT */}
          <View style={styles.container}>
            <ScrollView style={styles.scroll} showsVerticalScrollIndicator>

              {/* INTRO */}
              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.intro")}
              </Text>

              {/* 1 — WHAT */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.what.title")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.what.body")}
              </Text>

              {renderList(
                I18n.t("privacyPolicy.sections.what.list")
              )}

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.what.footer")}
              </Text>

              {/* 2 — WHY */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.why.title")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.why.body")}
              </Text>

              {/* 3 — WHO */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.who.title")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.who.body")}
              </Text>

              <View style={styles.companiesTable}>
                <View style={styles.tableRow}>
                  <View style={[styles.tableCell, styles.tableCellTopLeft]}>
                    {renderCompanyCell("company1")}
                  </View>
                  <View style={[styles.tableCell, styles.tableCellTopRight]}>
                    {renderCompanyCell("company2")}
                  </View>
                </View>
                <View style={styles.tableRow}>
                  <View style={[styles.tableCell, styles.tableCellBottomLeft]}>
                    {renderResponsibilityCell("company1")}
                  </View>
                  <View style={[styles.tableCell, styles.tableCellBottomRight]}>
                    {renderResponsibilityCell("company2")}
                  </View>
                </View>
              </View>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.who.dpo.title")}
              </Text>

              <Text style={styles.block}>
                {I18n.t("privacyPolicy.sections.who.dpo.name")}
                {"\n"}
                {I18n
                  .t("privacyPolicy.sections.who.dpo.address").join("\n")}
                {"\n"}
                <Text style={{color: "#0e2288"}}>{I18n.t("privacyPolicy.sections.who.dpo.email")}</Text>
              </Text>

              <Text style={styles.paragraph}>
                {I18n
                  .t("privacyPolicy.sections.who.processors").join("\n")}
                
                <Text style={{color: "#0e2288"}} href={I18n.t("privacyPolicy.sections.who.processorsLinkURL")} target="_blank">{I18n.t("privacyPolicy.sections.who.processorsLinkText")}</Text>
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.who.provider")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.who.transfer.intro")}
              </Text>

              <Text style={styles.paragraph}>
                {`• ${I18n.t("privacyPolicy.sections.who.transfer.list")[0]}`}<Text style={{color: "#0e2288"}} href={I18n.t("privacyPolicy.sections.who.transfer.link")} target="_blank">{I18n.t("privacyPolicy.sections.who.transfer.linkText")}</Text>
              </Text>
              <Text style={styles.paragraph}>
                • {I18n.t("privacyPolicy.sections.who.transfer.list")[1]}
              </Text>
              {/* {renderList(
                I18n.t("privacyPolicy.sections.who.transfer.list")
              )} */}

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.who.transfer.outro")}
              </Text>

              {/* 4 — LEGAL BASIS */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.legal.title")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.legal.body")}
              </Text>

              {/* 5 — RETENTION */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.retention.title")}
              </Text>

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.retention.body")}
              </Text>

              {/* 6 — RIGHTS */}
              <Text style={styles.sectionTitle}>
                {I18n.t("privacyPolicy.sections.rights.title")}
              </Text>
              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.rights.subtitle")}
              </Text>

              {renderRights(
                I18n.t("privacyPolicy.sections.rights.list"),
                I18n.t("privacyPolicy.sections.rights.explanations")
              )}

              <Text style={styles.paragraph}>
                {I18n.t("privacyPolicy.sections.rights.footer")}
              </Text>

              {/* META */}
              <Text style={styles.meta}>
                {I18n.t("privacyPolicy.meta.date")}
              </Text>
              <Text style={styles.meta}>
                {I18n.t("privacyPolicy.meta.version")}
              </Text>

            </ScrollView>
          </View>
        </View>
      </View>
    </LayoutWrapper>
  );
};

export default PrivacyPolicy;


const styles = StyleSheet.create({
  wrapper: {
    height: "100%",
    width: "90%",
  },
  header: {
    paddingBottom: "2%",
    paddingLeft: "2%",
  },
  title: {
    ...Fonts.pageTitle,
    color: "#0e2288",
    fontSize: 32,
    fontFamily: "Birkentype"
  },
  container: {
    width: "85%",
    height: "80%",
    alignSelf: "center",
    backgroundColor: "white",
    padding: "2%",
  },
  scroll: {
    width: "100%",
  },
  sectionTitle: {
    ...Fonts.text,
    fontSize: "1.5rem",
    fontWeight: "600",
    marginTop: "2%",
    marginBottom: "0.8%",
    fontFamily: "Geist-Light"
  },
  paragraph: {
    ...Fonts.text,
    fontSize:"1rem",
    marginBottom: "1%",
    lineHeight: "2",
    fontFamily: "Geist-Light"
  },
  bullet: {
    ...Fonts.text,
    fontSize:"1rem",
    marginLeft: 12,
    marginBottom: "0.5%",
    fontFamily: "Geist-Light"
  },
  block: {
    ...Fonts.text,
    fontSize:"1rem",
    marginBottom: "1%",
    lineHeight: "2",
    fontFamily: "Geist-Light"
  },
  meta: {
    ...Fonts.text,
    fontSize: 16,
    marginTop: "1%",
    fontFamily: "Geist-Light"
  },
  rightTitle: {
    fontWeight: "600",
    fontFamily: "Geist-Light",
  },
  companiesTable: {
    width: "100%",
    marginBottom: "1.5%",
    borderWidth: 1,
    borderColor: "#0e2288",
  },
  tableRow: {
    flexDirection: "row",
    width: "100%",
  },
  tableCell: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    justifyContent: "flex-start",
  },
  tableCellTopLeft: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#0e2288",
  },
  tableCellTopRight: {
    borderBottomWidth: 1,
    borderColor: "#0e2288",
  },
  tableCellBottomLeft: {
    borderRightWidth: 1,
    borderColor: "#0e2288",
  },
  tableCellBottomRight: {
    borderColor: "#0e2288",
  },
  tableCellTitle: {
    fontFamily: "Geist-Light",
    fontSize: 16,
    fontWeight: "600",
    lineHeight: 22,
    marginBottom: 6,
    color: "#0e2288",
  },
  tableCellText: {
    fontFamily: "Geist-Light",
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 4,
  },
});
